import { useNavigate, useLocation } from "react-router-dom";
import { UserPlus, LogIn } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Dialog shown when a signed-out visitor tries to perform an action that
 * requires a Lendly account (renting an item, listing an item, opening
 * the rentals / listings dashboards).
 *
 * Renders two CTAs — "Log in" and "Create an account" — and preserves the
 * page they were on via `location.state.from` so signup / login can bounce
 * them straight back to where they were.
 */
export const SignInRequiredDialog = ({
    open,
    onOpenChange,
    action = "continue",
    // Optional override for where to return after auth. Defaults to the
    // current path so a guest clicking "Request to rent" from /browse ends
    // up back on /browse after signing up.
    returnTo,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const target = returnTo || location.pathname + location.search;

    const go = (path) => {
        onOpenChange(false);
        navigate(path, { state: { from: target } });
    };

    const title = `Sign in to ${action}`;
    const description =
        "You'll need a free Lendly account to borrow items, list your own gear, or open your dashboards. Takes about a minute.";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="sign-in-required-dialog"
                className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 text-[#2e3220]"
            >
                <div className="p-6">
                    <DialogHeader className="text-left">
                        <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                            <UserPlus className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <DialogTitle
                            className="font-display text-2xl font-bold text-[#2e3220]"
                            data-testid="sign-in-required-title"
                        >
                            {title}
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-relaxed text-[#5a6142]">
                            {description}
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="mt-4 space-y-2 text-xs text-[#5a6142]">
                        <li className="flex items-start gap-2">
                            <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                            Free to join — no card required to browse or list.
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                            Message neighbors, request rentals, track everything from one dashboard.
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#737a3d]" />
                            Payments processed securely through Stripe — never over cash or DM.
                        </li>
                    </ul>
                    <DialogFooter className="mt-6 flex-col-reverse gap-2 sm:flex-row">
                        <Button
                            variant="ghost"
                            data-testid="sign-in-required-login"
                            onClick={() => go("/login")}
                            className="text-[#2e3220] hover:bg-[#2e3220]/5"
                        >
                            <LogIn className="mr-2 h-4 w-4" strokeWidth={2.5} />
                            Log in
                        </Button>
                        <Button
                            data-testid="sign-in-required-signup"
                            onClick={() => go("/signup")}
                            className="bg-[#737a3d] text-[#F4F1EA] hover:bg-[#5a6142]"
                        >
                            <UserPlus className="mr-2 h-4 w-4" strokeWidth={2.5} />
                            Create an account
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SignInRequiredDialog;
